import {accountingRequest} from '../services/request';
import {accountingEndpoints} from '../../api';
import {accountingConstants} from '../constants';
import {suggestionRecordTypeConstants} from '../../constants/accounting.constants';
import {apiHelpers} from '../../utils/helpers';

const errorHandlerProps = {disableAlert: true};

const createFetchAction = (url, actionType) => {
  return ({ query = null, successCallback, errorCallback } = {}) => {
    return (dispatch) => {
      accountingRequest.get({
        dispatch,
        url,
        query,
        successCallback: (data) => {
          dispatch({ type: actionType, data });
          successCallback?.(data);
        },
        errorCallback,
        errorHandlerProps
      });
    };
  };
};

const getCostCenters = createFetchAction(
  accountingEndpoints.GET_COST_CENTERS,
  accountingConstants.GET_COST_CENTERS
);

const getExpenseCategories = createFetchAction(
  accountingEndpoints.GET_EXPENSE_CATEGORIES,
  accountingConstants.GET_EXPENSE_CATEGORIES
);

const getLocations = createFetchAction(
  accountingEndpoints.GET_LOCATIONS,
  accountingConstants.GET_LOCATIONS
);

const getListItems = createFetchAction(
  accountingEndpoints.GET_LIST_ITEMS,
  accountingConstants.GET_LIST_ITEMS
);

const getNumberingFlows = createFetchAction(
  accountingEndpoints.GET_NUMBERING_FLOWS,
  accountingConstants.GET_NUMBERING_FLOWS
);

const getTaxCodes = createFetchAction(
  accountingEndpoints.GET_TAX_CODES,
  accountingConstants.GET_TAX_CODES
);

const getVendorExpenseAccounts = createFetchAction(
  accountingEndpoints.GET_VENDOR_EXPENSE_ACCOUNTS,
  accountingConstants.GET_VENDOR_EXPENSE_ACCOUNTS
);

const getCreditAccounts = createFetchAction(
  accountingEndpoints.GET_CREDIT_ACCOUNTS,
  accountingConstants.GET_CREDIT_ACCOUNTS
);

const getCustomSegments = createFetchAction(
  accountingEndpoints.GET_CUSTOM_SEGMENTS,
  accountingConstants.GET_CUSTOM_SEGMENTS
);

/**
 * The company's accounting settings, carrying `is_export_mode`.
 *
 * ⚠ Deliberately NOT createFetchAction. The mode decides whether the accounting section is
 * NetSuite-branded or Manual, and the UI now waits for it rather than painting NetSuite first
 * (H1MVP-4791) — so a failed request must still mark it resolved. Otherwise one 500 hides the
 * accounting section from a NetSuite company indefinitely, which is far worse than the flash.
 */
// How long to wait before giving up on the mode and falling back to NetSuite. Generous: this is a
// last resort for a request that never settles at all, not a latency budget.
const COMPANY_SETTINGS_TIMEOUT_MS = 15000;

const getCompanySettings = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    // ⚠ MUST resolve exactly once, and MUST resolve no matter how the request ends.
    //
    // `companySettingsResolved` gates the whole accounting section: until it is true the nav hides
    // it and three pages render a spinner (H1MVP-4791). So a path that leaves it false is not a
    // degraded mode, it is the section vanishing for the rest of the session — and there were two.
    //
    //   1. `successHandler` (request.js) calls NEITHER callback when a 200 body carries an `errors`
    //      key and `hideKeylessError` is unset, which it is here. The request finishes, and nothing
    //      downstream ever hears about it. `finallyCallback` covers that.
    //   2. axios has no timeout configured, so a request that never settles never reaches `finally`
    //      either. The watchdog covers that.
    //
    // Falling back to NetSuite rather than staying blank is deliberate: NetSuite is what a company
    // with nothing set already gets, so an unknown mode costs a NetSuite company nothing and costs
    // a Manual one a wrong label — the opposite trade of hiding the section from everybody.
    let settled = false;
    const resolve = (data) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      dispatch({type: accountingConstants.GET_COMPANY_SETTINGS, data});
    };
    const watchdog = setTimeout(() => resolve(null), COMPANY_SETTINGS_TIMEOUT_MS);

    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_COMPANY_SETTINGS,
      query,
      successCallback: (data) => {
        resolve(data);
        successCallback?.(data);
      },
      errorCallback: (error) => {
        // Resolved, but with nothing in it: the mode falls back to NetSuite, which is the safe
        // default for a company that already had it.
        resolve(null);
        errorCallback?.(error);
      },
      // Backstop for the neither-callback path. A no-op when one of them already resolved.
      finallyCallback: () => resolve(null),
      errorHandlerProps
    });
  };
};

// The company's own ERP field definitions. Fetched once and shared: the Company Settings tab
// manages them, and the accounting tables render a column per definition that applies to them.
const getAccountingFields = createFetchAction(
  accountingEndpoints.GET_ACCOUNTING_FIELDS,
  accountingConstants.GET_ACCOUNTING_FIELDS
);

const createAccountingField = ({data, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_ACCOUNTING_FIELD,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

// The API takes the create schema here, so this is a FULL REPLACE rather than a partial update —
// send every field, including the ones that did not change, or they are dropped.
const updateAccountingField = ({id, data, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.PATCH_ACCOUNTING_FIELD, {id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

// One endpoint per object type, but the call is identical, so the record type picks the URL rather
// than each page carrying its own copy of this.
const CUSTOM_FIELD_VALUE_ENDPOINTS = {
  [suggestionRecordTypeConstants.JOURNAL_ENTRY]: accountingEndpoints.PATCH_JOURNAL_ENTRY_CUSTOM_FIELD_VALUE,
  [suggestionRecordTypeConstants.VENDOR]: accountingEndpoints.PATCH_VENDOR_CUSTOM_FIELD_VALUE,
  [suggestionRecordTypeConstants.VENDOR_BILL]: accountingEndpoints.PATCH_INVOICE_CUSTOM_FIELD_VALUE,
};

/**
 * Sets one custom field's value on one record.
 *
 * ⚠ The API takes a single field per request — there is no batch form — so editing three fields on
 * a row is three calls. The stored value for a dropdown is the option's `external_id`, not its
 * label: that is what the export emits and what the ERP matches on.
 */
const updateCustomFieldValue = ({recordType, id, fieldId, value, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    const url = CUSTOM_FIELD_VALUE_ENDPOINTS[recordType];
    if (!url) return;

    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(url, {id, field_id: fieldId}),
      // ⚠ The API takes `value` as a STRING for every field type, number included:
      // PATCH {value: 7} is a 422 ("Input should be a valid string"), PATCH {value: "7"} is a 200.
      // An Ant InputNumber hands back a JS number, so without this the write fails silently — the
      // request 422s, the row never gets the value, and the only visible symptom is that Register
      // stays blocked. Coerced here because this is the one writer both the bulk Classify and the
      // inline cell editor go through (H1MVP-4790).
      data: {value: value === null || value === undefined ? value : String(value)},
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

// Register is the export-mode replacement for Sync: it marks rows and stamps a wl_reference, and
// it does NOT download anything. Each object type has its own endpoint and its own id key, but the
// call is otherwise identical — so the record type picks both rather than each page carrying a copy.
const REGISTER_ENDPOINTS = {
  [suggestionRecordTypeConstants.JOURNAL_ENTRY]: {
    url: accountingEndpoints.POST_REGISTER_JOURNAL_ENTRIES,
    idsKey: 'journal_entry_ids',
  },
  [suggestionRecordTypeConstants.VENDOR]: {
    url: accountingEndpoints.POST_REGISTER_VENDORS,
    idsKey: 'vendor_ids',
  },
  [suggestionRecordTypeConstants.VENDOR_BILL]: {
    url: accountingEndpoints.POST_REGISTER_INVOICES,
    idsKey: 'invoice_ids',
  },
};

/**
 * Register the given records.
 *
 * ⚠ The response is a bulk result — `{updated: [...], failed: [{id, error}]}` — so a 200 does NOT
 * mean every row succeeded. Callers must read `failed` rather than treating the status as the
 * answer.
 */
const registerAccountingRecords = ({recordType, ids, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    const endpoint = REGISTER_ENDPOINTS[recordType];
    if (!endpoint || !ids?.length) return;

    accountingRequest.post({
      dispatch,
      url: endpoint.url,
      data: {[endpoint.idsKey]: ids},
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const deleteAccountingField = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.DELETE_ACCOUNTING_FIELD, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const createInvoice = ({data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_CREATE_INVOICE,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const createVendor = ({data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_CREATE_VENDOR,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const deleteInvoice = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.DELETE_INVOICE, {id}),
      successCallback,
      errorCallback
    });
  }
}

const deleteJournalEntry = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.DELETE_JOURNAL_ENTRY, {id}),
      successCallback,
      errorCallback
    });
  }
}

const deleteVendor = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.DELETE_VENDOR, {id}),
      successCallback,
      errorCallback
    });
  }
}

const getJournalEntries = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_JOURNAL_ENTRIES,
      query,
      successCallback,
      errorCallback
    });
  }
}

const getJournalEntriesTotals = ({successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_JOURNAL_ENTRIES_STATS,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getJournalEntry = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.GET_JOURNAL_ENTRY, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getJournalEntryExpenses = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.GET_JOURNAL_ENTRY_EXPENSES, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const exportJournalEntries = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_JOURNAL_ENTRIES_EXPORT,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getVendor = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.GET_VENDOR, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getVendors = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_VENDORS,
      query,
      successCallback,
      errorCallback
    });
  }
}

const getVendorsTotals = ({successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_VENDORS_STATS,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const exportVendors = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_VENDORS_EXPORT,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getInvoice = ({id, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.GET_INVOICE, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getInvoices = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_INVOICES,
      query,
      successCallback,
      errorCallback
    });
  }
}

const getInvoicesTotals = ({successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_INVOICES_STATS,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const exportInvoices = ({query = null, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: accountingEndpoints.GET_INVOICES_EXPORT,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const updateJournalEntry = ({id, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.PATCH_UPDATE_JOURNAL_ENTRY, {id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const updateInvoice = ({id, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.PATCH_UPDATE_INVOICE, {id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const updateVendor = ({id, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.PATCH_UPDATE_VENDOR, {id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const syncInvoice = ({id, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.POST_SYNC_INVOICE, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const syncVendor = ({id, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.POST_SYNC_VENDOR, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const readyToSyncJournalEntries = ({ids, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_READY_TO_SYNC_JOURNAL_ENTRIES,
      data: { journal_entry_ids: ids },
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const syncInvoices = ({ids, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_SYNC_INVOICES,
      data: { invoice_ids: ids },
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const syncJournalEntries = ({ids, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_SYNC_JOURNAL_ENTRIES,
      data: { journal_entry_ids: ids },
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const syncVendors = ({ids, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: accountingEndpoints.POST_SYNC_VENDORS,
      data: { vendor_ids: ids },
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const batchDeleteVendors = ({data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: accountingEndpoints.DELETE_BATCH_VENDORS,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const classifyVendors = ({ids, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: accountingEndpoints.PATCH_BATCH_VENDORS,
      data: {ids, data},
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const batchDeleteJournalEntries = ({data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: accountingEndpoints.DELETE_BATCH_JOURNAL_ENTRIES,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const batchDeleteInvoices = ({data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.delete({
      dispatch,
      url: accountingEndpoints.DELETE_BATCH_INVOICES,
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const classifyJournalEntries = ({ids, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: accountingEndpoints.PATCH_BATCH_JOURNAL_ENTRIES,
      data: {ids, data},
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const classifyInvoices = ({ids, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: accountingEndpoints.PATCH_BATCH_INVOICES,
      data: {ids, data},
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getSuggestions = ({record_type, record_id, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.get({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.GET_SUGGESTIONS, {record_type, record_id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const postSuggestionFeedback = ({record_type, record_id, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.POST_SUGGESTION_FEEDBACK, {record_type, record_id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const splitJournalEntry = ({id, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.post({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.POST_SPLIT_JOURNAL_ENTRY, {id}),
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const resetJournalEntry = ({id, data, successCallback, errorCallback}) => {
  return (dispatch) => {
    accountingRequest.patch({
      dispatch,
      url: apiHelpers.getUrl(accountingEndpoints.PATCH_RESET_JOURNAL_ENTRY, {id}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

export const accountingActions = {
  getCostCenters,
  getAccountingFields,
  createAccountingField,
  updateAccountingField,
  deleteAccountingField,
  updateCustomFieldValue,
  registerAccountingRecords,
  getCompanySettings,
  getCustomSegments,
  getListItems,
  getLocations,
  getExpenseCategories,
  getNumberingFlows,
  getTaxCodes,
  getVendorExpenseAccounts,
  getCreditAccounts,

  createInvoice,
  createVendor,
  deleteInvoice,
  deleteJournalEntry,
  deleteVendor,
  getJournalEntries,
  getJournalEntriesTotals,
  getJournalEntry,
  getJournalEntryExpenses,
  exportJournalEntries,
  getInvoice,
  getInvoices,
  getInvoicesTotals,
  exportInvoices,
  getSuggestions,
  postSuggestionFeedback,
  getVendor,
  getVendors,
  getVendorsTotals,
  exportVendors,
  updateJournalEntry,
  updateInvoice,
  updateVendor,
  readyToSyncJournalEntries,
  splitJournalEntry,
  syncInvoice,
  syncInvoices,
  syncJournalEntries,
  syncVendor,
  syncVendors,
  batchDeleteVendors,
  classifyVendors,
  batchDeleteJournalEntries,
  batchDeleteInvoices,
  classifyJournalEntries,
  classifyInvoices,
  resetJournalEntry,
};
